import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { FileImage, FilePlus2, FileText } from 'lucide-react';
import { api, ApiError } from '../../lib/api';
import { formatDateTime } from '../../lib/dateFmt';
import { AddDocumentModal } from '../person/AddDocumentModal';
import { Button } from '../ui/Button';
import { EmptyState } from '../ui/EmptyState';
import type { MeDocument } from './meTypes';

/**
 * The inductee's own documents (driving licence photos, certs, form and
 * upload files), with self-service add via the camera modal.
 */
export function DocumentsCard({
  documents,
  personName,
}: {
  documents: MeDocument[];
  personName: string;
}) {
  const [adding, setAdding] = useState(false);
  const qc = useQueryClient();
  const upload = useMutation({
    mutationFn: async (input: {
      name: string;
      description?: string;
      dataUrl: string;
    }) =>
      api('/api/me/documents', {
        method: 'POST',
        body: JSON.stringify({
          name: input.name,
          ...(input.description ? { description: input.description } : {}),
          document_jpeg: input.dataUrl,
        }),
      }),
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: ['me-view'] });
    },
  });

  async function onSave(input: {
    name: string;
    description?: string;
    dataUrl: string;
  }) {
    try {
      await upload.mutateAsync(input);
      toast.success(`${input.name} saved`);
      setAdding(false);
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : 'Could not save the document');
      throw err;
    }
  }

  return (
    <section className="overflow-hidden rounded-token-lg border border-line/10 bg-surface shadow-token-sm">
      <div className="flex items-center justify-between gap-2 border-b border-divider/10 px-5 py-3">
        <div className="flex items-center gap-2">
          <FileImage className="h-4 w-4 text-accent" />
          <h2 className="text-sm font-semibold text-fg">Your documents</h2>
        </div>
        <Button
          variant="secondary"
          size="sm"
          iconLeft={<FilePlus2 className="h-4 w-4" />}
          onClick={() => setAdding(true)}
        >
          Add
        </Button>
      </div>
      {documents.length === 0 ? (
        <EmptyState
          title="No documents on file"
          description="Take a photo of your driving licence, certs, or anything else you'd like on file."
        />
      ) : (
        <ul className="divide-y divide-divider/10">
          {documents.map((d) => {
            // DocumentAttached docs stream from /api/documents; form/upload
            // files (any type) come from the self-scoped onboarding route.
            const href =
              d.source === 'document'
                ? `/api/documents/${d.key}`
                : `/api/me/onboarding/files/${d.key}`;
            const isImage = (d.content_type ?? '').startsWith('image/');
            return (
              <li key={d.id} className="flex items-start gap-3 px-5 py-3">
                <a
                  href={href}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex h-16 w-16 flex-none items-center justify-center overflow-hidden rounded-token-sm bg-surface-2 ring-1 ring-line/10 transition-colors hover:ring-accent/30"
                  aria-label={`Open ${d.name}`}
                >
                  {isImage ? (
                    <img
                      src={href}
                      alt={d.name}
                      className="h-full w-full object-cover"
                      loading="lazy"
                    />
                  ) : (
                    <FileText className="h-6 w-6 text-fg-dim" />
                  )}
                </a>
                <div className="min-w-0 flex-1">
                  <div className="truncate text-sm font-semibold text-fg">
                    {d.name}
                  </div>
                  {d.description ? (
                    <p className="mt-0.5 whitespace-pre-wrap text-xs text-fg-muted">
                      {d.description}
                    </p>
                  ) : null}
                  {d.at ? (
                    <div className="mt-1 text-[11px] tabular-nums text-fg-dim">
                      Added {formatDateTime(d.at)}
                    </div>
                  ) : null}
                </div>
                <a
                  href={href}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-xs font-medium text-accent hover:underline"
                >
                  Open
                </a>
              </li>
            );
          })}
        </ul>
      )}
      <AddDocumentModal
        open={adding}
        personName={personName}
        onClose={() => setAdding(false)}
        onSave={onSave}
      />
    </section>
  );
}
